/* eslint-disable max-len */
import React, { ReactElement, useState } from 'react';
import { isEmpty } from 'ts-utils';
import { LayoutScreenConfig, ScreenType } from '../../config/FlowTypes';
import {
  INSURANCE_PROVIDERS_AU,
  INSURANCE_PROVIDERS_US_MEDICARE_FIRST,
  NOT_LISTED_OPTION,
  getInsuranceDiscount,
} from '../../config/InsuranceProviders';
import { ActionType } from '../../store/ActionTypes';
import { store, useAppStore } from '../../store/Store';
import { dispatchUserData } from '../../store/userData/UserDataActions';
import { Analytics } from '../../util/Analytics';
import { isEligibleForFree65Access, isUsNon65Plus } from '../../util/MedicareAccess';
import { ScreenWrapper } from '../styles/Containers';
import { NavigationProps } from './FlowNavigation';
import {
  CoverageCheckConsentValues,
  CoverageCheckConsents,
  CoverageCheckLayoutConfig,
} from './layouts/CoverageCheckConsents';
import { DropdownOption, DropdownSelectLayout } from './layouts/DropdownSelectLayout';
import { LayoutActions } from './layouts/LayoutActions';

export type InsuranceProviderLayoutConfig = {
  title?: string;
  question: string;
  primaryButtonTitle: string;
  /** Overrides the geolocation-based provider list (e.g. a Medicare Advantage plan list). */
  providers?: string[];
  /**
   * Whether selecting a provider applies the insurance discount coupon. Defaults to true. Set false
   * on referral flows that already carry their own coupon in the URL, so it isn't overwritten.
   */
  applyInsuranceCoupon?: boolean;
  /**
   * "Check my coverage" variant for US non-65+ users (the coverage-flow audience): replaces the copy
   * and button title, requires two consents, and adds an "I'll pay myself" exit that skips the
   * coverage screens. Ignored for everyone else, who see the plain dropdown.
   */
  coverageCheck?: CoverageCheckLayoutConfig;
};

const CONSENT_YES = 'Yes';
const CONSENT_NO = 'No';

export type InsuranceProviderScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.InsuranceProvider,
  InsuranceProviderLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  InsuranceProviderScreenConfig<ScreenNames>,
  ScreenNames
>;

function getInsuranceProvidersForCountry(country: string): string[] {
  return country === 'AU' ? INSURANCE_PROVIDERS_AU : INSURANCE_PROVIDERS_US_MEDICARE_FIRST;
}

export const InsuranceProviderScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const { navigation, screenName, layout } = props;
  const country = useAppStore((state) => state.geolocation.country);
  const insuranceProviders = layout.providers ?? getInsuranceProvidersForCountry(country);
  const inCoverageAudience = useAppStore(isUsNon65Plus);
  const coverageCheck = inCoverageAudience ? layout.coverageCheck : undefined;
  const storedAuthorization = useAppStore(
    (state) => state.userData.coverageCheckAuthorization?.[0] === CONSENT_YES
  );
  const storedAssignment = useAppStore(
    (state) => state.userData.coverageAssignmentOfBenefits?.[0] === CONSENT_YES
  );
  const [consents, setConsents] = useState<CoverageCheckConsentValues>({
    authorization: storedAuthorization,
    assignmentOfBenefits: storedAssignment,
  });
  const consentsGiven =
    coverageCheck === undefined || (consents.authorization && consents.assignmentOfBenefits);

  const options: DropdownOption[] = [
    { value: 'separator', label: '──────────', disabled: true },
    ...insuranceProviders,
    NOT_LISTED_OPTION,
  ];

  // Local state to track if button should be enabled
  const [selectedValue, setSelectedValue] = React.useState<string>('');

  function onSelected(selected: string) {
    setSelectedValue(selected);
  }

  function trackAnswer(question: string, answer: string | string[]): void {
    Analytics.track({ event: Analytics.Event.o_question_answered, question, answer });
  }

  function onSubmitPress() {
    if (isEmpty(selectedValue) || !consentsGiven) return;

    dispatchUserData({ [screenName]: [selectedValue] });
    trackAnswer(screenName, [selectedValue]);

    if (coverageCheck !== undefined) {
      // Both consents gate the button, so these are Yes here; No is written by the self-pay exit.
      const authorization = consents.authorization ? CONSENT_YES : CONSENT_NO;
      const assignmentOfBenefits = consents.assignmentOfBenefits ? CONSENT_YES : CONSENT_NO;
      dispatchUserData({
        coverageCheckAuthorization: [authorization],
        coverageAssignmentOfBenefits: [assignmentOfBenefits],
        coverageSelfPay: [CONSENT_NO],
      });
      trackAnswer('coverageCheckAuthorization', authorization);
      trackAnswer('coverageAssignmentOfBenefits', assignmentOfBenefits);
    }

    // When a screen opts out, leave the coupon already in pricing state untouched (e.g. the referral
    // coupon set from the URL's `?coupon=` param) rather than overwriting it with the insurance
    // discount.
    if (
      layout.applyInsuranceCoupon !== false &&
      isEligibleForFree65Access(store.getState()) === false
    ) {
      store.dispatch({
        type: ActionType.SET_COUPON_CODE,
        couponCode: getInsuranceDiscount().couponCode,
      });
    }

    navigation.next(props);
  }

  /**
   * "I'll pay for Nerva myself": records the choice (and the provider, if one was picked) and lets the
   * entry selector fall through to the config's non-coverage exit. No consents are needed.
   */
  function onSelfPayPress() {
    dispatchUserData({
      coverageSelfPay: [CONSENT_YES],
      ...(isEmpty(selectedValue) ? {} : { [screenName]: [selectedValue] }),
    });
    trackAnswer('coverageSelfPay', CONSENT_YES);
    if (!isEmpty(selectedValue)) trackAnswer(screenName, [selectedValue]);
    navigation.next(props);
  }

  return (
    <ScreenWrapper>
      <DropdownSelectLayout
        heading={coverageCheck?.title ?? layout.title}
        question={coverageCheck?.question ?? layout.question}
        options={options}
        onSelected={onSelected}
      />
      {coverageCheck !== undefined && (
        <CoverageCheckConsents config={coverageCheck} values={consents} onChange={setConsents} />
      )}
      <LayoutActions
        primaryButtonTitle={coverageCheck?.primaryButtonTitle ?? layout.primaryButtonTitle}
        onPressPrimaryButton={onSubmitPress}
        primaryButtonDisabled={isEmpty(selectedValue) || !consentsGiven}
        secondaryButtonTitle={coverageCheck?.selfPayTitle}
        onPressSecondaryButton={coverageCheck === undefined ? undefined : onSelfPayPress}
        secondaryButtonPlacement="below"
        secondaryButtonTestId="coverage-self-pay"
      />
    </ScreenWrapper>
  );
};
